import { Component, Input, OnInit } from '@angular/core';
import { ModelService } from '../Services/model.service';
import { TranslateService } from '@ngx-translate/core';
import { Template } from '../Model/Catalog/Template';
import { TemplateService } from '../Services/template.service';
import { lastValueFrom } from 'rxjs';
import {
  ColumnCellTemplateData,
  RowPreparedEvent,
  RowRemovedEvent,
  RowUpdatedEvent,
  ToolbarPreparingEvent
} from 'devextreme/ui/data_grid';
import { ValueChangedEvent } from 'devextreme/ui/check_box';
import { PimLicense } from '../Model/User/PimLicense';
import { LoginService } from 'app/Services/login.service';
import { Router } from '@angular/router';
import { SystemService } from 'app/Services/system.service';
import { Customer } from 'app/Model/User/Customer';
import { TemplateItem } from '../Model/Catalog/TemplateItem';
import { NextPimField } from '../Model/Catalog/NextPimField';

@Component({
  selector: 'templateList',
  templateUrl: 'templateList.component.html',
  styleUrls: ['./templateList.component.css']
})
export class TemplateListComponent implements OnInit {
  @Input() containerId: string = null;

  public canAddTemplate: boolean = false;

  exportMode: boolean = false;
  editorVisible: boolean = false;
  txtDuplikat: string = null;
  addTemplateVisible = false;
  addAdvancedTemplateVisible = false;
  templates: Template[] = [];

  get hasAddonAdvancedTemplate() {
    return this.loginService.hasAddonAdvancedTemplate;
  }

  get customerId() {
    return this.loginService.currentUser.customerId;
  }

  get isOnboarding(): boolean {
    return this.loginService.hasAddonONBOARDING;
  }

  isSelectCustomerVisible: boolean;
  templateIdToCopy: string;
  customers: Customer[];
  selectedCustomers: string[];

  constructor(
    public loginService: LoginService,
    public systemService: SystemService,
    public templateService: TemplateService,
    public translate: TranslateService,
    public router: Router
  ) {
    this.getAddTemplateHint = this.getAddTemplateHint.bind(this);
  }

  async ngOnInit() {
    if (this.loginService.currentUser == null) {
      this.router.navigate(['/']);
      return;
    }

    if (this.router.url === '/templates') {
      this.systemService.currentNavigationTitle = this.translate.instant('Excel Templates');
    }
    if (this.loginService.isAdmin) {
      this.loginService.customerService.getAllCustomerByUser(this.loginService.currentUser.id).subscribe((customers: Customer[]) => {
        this.customers = new Array<Customer>();
        customers.forEach((customer: Customer) => {
          if (customer.id != this.loginService.currentCustomer.id) {
            this.customers.push(customer);
          }
        });
      });
    }

    await this.loadTemplateList();

    this.txtDuplikat = this.translate.instant('Duplikat');

    this.canAddTemplate = await lastValueFrom(
      this.templateService.canCustomerAddTemplate(this.loginService.currentCustomer.id)
    );
  }

  async loadTemplateList() {
    await lastValueFrom(this.templateService.getTemplates());
    this.templates = this.templateService.templates?.filter(x => (x.containerId === this.containerId || this.containerId === null)) ?? [];
  }

  async updateTemplateList(newTemplate: Template) {
    const templateId = await this.templateService.addTemplate(newTemplate);
    this.templates = this.templateService.templates?.filter(x => (x.containerId === this.containerId || this.containerId === null)) ?? [];
    return templateId;
  }

  public onLogoClicked() {
    this.router.navigate(['/catalogs']);
  }

  onToolbarPreparing(event: ToolbarPreparingEvent<Template>) {
    event.toolbarOptions.items.unshift({
      location: 'before',
      locateInMenu: 'auto',
      template: 'newTemplateButtonTemplate'
    });
    event.toolbarOptions.items.unshift({
      location: 'after',
      locateInMenu: 'auto',
      template: 'helpButtonTemplate'
    });
  }

  showAddTemplate() {
    this.addTemplateVisible = true;
  }

  async addTemplate(name: string) {
    this.addTemplateVisible = false;

    let newTemplate = new Template(); // data würde an sich auch gehen... vllt., ABER der CTOR initialisiert noch ein bissel was...
    newTemplate.lastChange = newTemplate.created; // Wird nicht vom CTOR gesetzt
    newTemplate.isExportTemplate = true;
    newTemplate.name = name;

    if (this.containerId !== null) {
      newTemplate.containerId = this.containerId;
    }

    await this.updateTemplateList(newTemplate);
    this.canAddTemplate = await lastValueFrom(
      this.templateService.canCustomerAddTemplate(this.loginService.currentCustomer.id)
    );
  }

  showAddAdvancedTemplate() {
    this.addAdvancedTemplateVisible = true;
  }

  async addAdvancedTemplate({ name, type }: { name: string; type: string }) {
    this.addAdvancedTemplateVisible = false;

    const newTemplate = new Template();
    newTemplate.lastChange = newTemplate.created;
    newTemplate.isExportTemplate = true;
    newTemplate.isAdvancedTemplate = true;
    newTemplate.useMetadata = type !== 'DEFAULT';
    newTemplate.templateType = type !== 'DEFAULT' ? type : null;
    newTemplate.name = name;

    if (this.containerId !== null) {
      newTemplate.containerId = this.containerId;
    }

    const templateId = await this.updateTemplateList(newTemplate);
    this.canAddTemplate = await lastValueFrom(
      this.templateService.canCustomerAddTemplate(this.loginService.currentCustomer.id)
    );

    await this.router.navigate(['/templates/expert', this.customerId, templateId]);
  }

  getAddTemplateHint() {
    if (this.canAddTemplate) return null;

    return this.translate.instant('In ihrer Lizenz dürfen Sie keine Templates mehr anlegen.');
  }

  rowPrepared(e: RowPreparedEvent<Template>) {
    if (e.rowType === 'data') {
      if (e.data.isAdvancedTemplate) {
        e.rowElement.classList.add('template-highlight');
      }
    }
  }

  allowChangeTemplateType(templates: TemplateItem[], isExportTemplate: boolean) {
    if (isExportTemplate) {
      return !templates.some(item => item.pimFields.length > 1);
    } else {
      let pimFields = templates.flatMap(item => item.pimFields);
      return new Set(pimFields).size !== pimFields.length;
    }
  }

  exportTemplateCheckboxChanged(event: ValueChangedEvent, cellData: ColumnCellTemplateData<Template>) {
    if (cellData.data.isExportTemplate) {
      let pimFields = cellData.data.templateItems.flatMap(item => item.pimFields);
      if (this.hasDuplicatePimField(pimFields)) {
        this.systemService.notifyWarning(this.translate.instant("Template-Typ kann nicht angepasst werden, da ein PIM-Feld mit zwei Spalten verknüpft ist."));
        return;
      }
    } else {
      if (cellData.data.templateItems.some(item => item.pimFields.length > 1)) {
        this.systemService.notifyWarning(this.translate.instant("Template-Typ kann nicht angepasst werden, da eine Spalte mit zwei PIM-Feldern verknüpft ist."));
        return;
      }
    }
    
    if (cellData.data.isAdvancedTemplate) {
      cellData.data.isExportTemplate = true;
      return;
    }
    const dataGrid = cellData.component;
    const rowIndex = cellData.rowIndex;
    dataGrid.cellValue(rowIndex, 'isExportTemplate', event.value);
    dataGrid.saveEditData();
  }

  hasDuplicatePimField(pimFields: NextPimField[]): boolean {
    const duplicates = new Set<string>();

    for (const field of pimFields) {
      const key = `${field.field}__${field.elementKey}__${field.systemKey}`;
      if (duplicates.has(key)) return true;
      duplicates.add(key);
    }

    return false;
  }

  rowUpdated(event: RowUpdatedEvent<Template>) {
    let template = event.data;
    template.lastChange = new Date();

    // Advanced Templates (alias Expertentemplates) dürfen nur Export Templates sein.
    if (template.isAdvancedTemplate) {
      template.isExportTemplate = true;
    }

    this.templateService.saveTemplate(template, true);
  }

  async rowRemoved(event: RowRemovedEvent<Template>) {
    let template = event.data as Template;

    await this.templateService.deleteTemplate(template);

    this.canAddTemplate = await lastValueFrom(
      this.templateService.canCustomerAddTemplate(this.loginService.currentCustomer.id)
    );
  }

  async duplicateTemplate(event: ColumnCellTemplateData<Template>) {
    if (!this.canAddTemplate) {
      this.systemService.notifyWarning(
        this.translate.instant('In ihrer Lizenz dürfen Sie keine Templates mehr anlegen.')
      );
      return;
    }

    let template = event.data;


    if (this.loginService.isAdmin) {
      this.isSelectCustomerVisible = true;
      this.templateIdToCopy = template.id;
    } else {
      let copiedTemplate = new Template(); // data würde an sich auch gehen... vllt., ABER der CTOR initialisiert noch ein bissel was...
      copiedTemplate.lastChange = copiedTemplate.created; // Wird nicht vom CTOR gesetzt
      copiedTemplate.isExportTemplate = template.isExportTemplate;
      copiedTemplate.isAdvancedTemplate = template.isAdvancedTemplate;

      copiedTemplate.customerId = template.customerId;
      copiedTemplate.name = template.name + ' - ' + this.txtDuplikat;
      copiedTemplate.remarks = template.remarks;
      copiedTemplate.isUseable = template.isUseable;
      copiedTemplate.templateItems = template.templateItems;
      copiedTemplate.containerId = template.containerId;

      copiedTemplate.useMetadata = template.useMetadata;
      copiedTemplate.metadata = template.metadata;
      copiedTemplate.worksheetItems = template.worksheetItems;
      copiedTemplate.isAdvancedTemplate = template.isAdvancedTemplate;
      copiedTemplate.templateType = template.templateType;

      await this.updateTemplateList(copiedTemplate);
      this.canAddTemplate = await lastValueFrom(
        this.templateService.canCustomerAddTemplate(this.loginService.currentCustomer.id)
      );
    }
  }

  copyTemplate(e) {
    this.isSelectCustomerVisible = false;
    this.selectedCustomers.forEach(async (customerId : string) => {
      await this.templateService.copyTemplate(this.loginService.currentCustomer.id, this.templateIdToCopy, customerId);
      this.canAddTemplate = await lastValueFrom(this.templateService.canCustomerAddTemplate(this.loginService.currentCustomer.id));
    });
  }

  canCopy(): boolean {
    if (this.selectedCustomers?.length == 0 || this.selectedCustomers == null) {
      return false;
    }
    return true;
  }

  editTemplate(event: ColumnCellTemplateData<Template>) {
    let template = event.data;
    if (template.isAdvancedTemplate) {
      this.router.navigate(['/templates/expert', this.customerId, template.id]);
    } else {
      this.editorVisible = true;
      this.templateService.template = template;
      this.exportMode = template.isExportTemplate;
    }
  }

  itemCountTemplate(cellData: ColumnCellTemplateData<Template>) {
    const template = cellData.data;
    if (template.isAdvancedTemplate) {
      const worksheetCount = template.worksheetItems.length;
      const itemCount = template.worksheetItems.reduce((a, b) => a + b.templateItems.length, 0);
      return `${worksheetCount}/${itemCount}`;
    }
    return template.templateItems.length;
  }
}
